import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import useSWR from 'swr';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Modal } from '@shared/components/modals/Modal';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { datediff, openPdf, parseDate, queryParams } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { IConfiguration } from '@modules/admin/AdminTools/ConfigurationTable/types';
import { selectWorkflowCurrentStep, setLastCommentsRefresh, setPayoffQuoteResponse } from '@store/reducers/contactManagerSlice';
import { ModalWrapper } from '@styles/StyledDiv';
import { colorTheme } from '@styles/muiTheme';
import { SERVICES } from '@shared/constants/apiConfig';
import { dateFormat } from '@shared/constants/formats';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Roles } from '@shared/constants/constants';
import { EConfirmationModalText, IAddAdjustment, IFinanceData, IFinanceModalProps, IPayoffQuote } from '../interfaces';
import { IDealSheetAdjustment } from '../../../interfaces';
import { adjustmentsGridColumns } from '../../constants';
import { createDealSheetAdjustment, requestCancelPayoff, requestPullFinalPayoffQuote, requestSubmitPayoffQuote, requestUpdateAssetLienholder, savePayoffQuote } from '../queries';
import { IAsset } from '../../../../Assets/interfaces';
import { FinanceForm } from './FinanceForm';

const allowedSteps = ['Pending Funding', 'Pending Funding Condition'];

export const initalAdjustmentValue: IAddAdjustment = {
  amountToFinanceFlat: 0,
  adjustedPayoff: 0,
  totalAdjustment: 0
};

export const FinanceModal = ({ financeModalPostition, assetId, handleClose, financeConditionItem }: IFinanceModalProps) => {
  const { isAdmin, userHasRoles } = useUserAccount();
  const dispatch = useDispatch();

  const hasPullFinalPayoffRole = isAdmin || userHasRoles([Roles.PULL_FINAL_PAYOFF]);
  const hasAcumaticaATSubmitRole = isAdmin || userHasRoles([Roles.ACUMATICA_AT_SUBMIT]);
  const hasAdminAccountingRole = isAdmin || userHasRoles([Roles.ACCOUNING]);
  const hasAdminAccountingCSRole = hasAdminAccountingRole || userHasRoles([Roles.CUSTOMER_SERVICE]);

  const { data: configurationTables } = useSWR<IConfiguration[]>('/configuration-service/configurations');
  const [isLoading, setIsLoading] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const workflowCurrentStep = useSelector(selectWorkflowCurrentStep);
  const [alertMessage, setAlertMessagee] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [showFirstCustomButton, setShowFirstCustomButton] = useState(false);
  const [showSecondCustomButton, setShowSecondCustomButton] = useState(false);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showCancelModal, setshowCancelModal] = useState(false);
  const [confirmationModalText, setConfirmationModalText] = useState(EConfirmationModalText.SubmitPayoffConfirmation);
  const [holidays, setHolidays] = useState<string[]>([]);
  const params = queryParams();
  const formikRef = useRef<FormikProps<IFinanceData> | null>(null);
  const [payoffData, setPayoffData] = useState<IPayoffQuote>({} as IPayoffQuote);

  const { data: asset, mutate } = useSWR<IAsset>(assetId && `${SERVICES.ASSET}/assets/${assetId}`);
  const { data: dealsheetAdjustment, mutate: mutateDealsheetAdjustment } = useSWR(`${SERVICES.ACCOUNTING}/accounting/dealsheetAdjustment/${queryParams()?.dealId}/`);
  const { data: initPayoffData, isLoading: payoffLoading, isValidating, error } = useSWR<IPayoffQuote>(`${SERVICES.ASSET}/contacts/${params.id}/assets/${params.assetId}/deals/${params.dealId}/payoff`);

  useEffect(() => {
    const holidayDates = configurationTables?.filter(configuration => configuration.configurationGroup === 'FINANCE_TAB' &&
      configuration.tokenName === 'BANK_HOLIDAYS');
    if (holidayDates) {
      setHolidays(holidayDates[0]?.value?.split(','));
    } else {
      setHolidays([]);
    }
  }, [configurationTables]);

  useEffect(() => {
    if (initPayoffData) {
      setPayoffData(initPayoffData);
    }

  }, [initPayoffData]);

  const salesPrice = useMemo(() => (financeConditionItem?.salesPrice ?? 0) as number, [financeConditionItem?.salesPrice]);

  const payoffQuoteData: IFinanceData = useMemo(
    () => {
      const tomorrow = new Date();
      const formattedGoodThruDate: string = moment(payoffData?.payoffGoodDate).format(dateFormat);
      tomorrow.setDate(tomorrow.getDate() + 1); //financing will process payment the next business day
      //keep adding a day until it's not a Saturday, Sunday or a holiday)
      while (tomorrow.getDay() == 0 || tomorrow.getDay() == 6 || holidays.includes((tomorrow.getMonth() + 1) + '/' + tomorrow.getDate())) {
        tomorrow.setDate(tomorrow.getDate() + 1);
      }
      const formattedTomorrowDate: string = moment(tomorrow).format(dateFormat);
      const diffDays = datediff(parseDate(formattedTomorrowDate), parseDate(formattedGoodThruDate));
      const perDiemValue = payoffData?.atTitleIsReleasable ? 0 : payoffData?.payoffPerDiem ?? 0;
      const totalPayoffValue = payoffData?.payoffAmount ? (payoffData.payoffAmount - (diffDays * perDiemValue)) || 0 as number : (payoffData?.totalPayoffAmount ?? 0) as number;
      const dtTotalDueValue = (payoffData?.dtTransactionFee || payoffData?.dtTaxes) ? (totalPayoffValue + (payoffData?.dtTransactionFee ?? 0) + (payoffData?.dtTaxes ?? 0)) : 0;
      let refundDue = (salesPrice - totalPayoffValue) as number;
      const dtRefundDueValue = payoffData?.atTitleIsReleasable ? (refundDue < 0 ? 0 : refundDue) : 0;
      let totalAdjustmentValue = 0;

      if (payoffData?.atTitleIsReleasable) {
        if (refundDue < 0.01) {
          const value = (salesPrice - totalPayoffValue - dtRefundDueValue) as number;
          totalAdjustmentValue = value < 0 ? -(value) : value;
          refundDue = 0;
        }
      }
      const payoffGoodDate = payoffData?.payoffGoodDate ? new Date(payoffData?.payoffGoodDate) : new Date();

      return ({
        adjustments: { ...initalAdjustmentValue },
        payOffData: {
          payoffQuoteId: payoffData?.payoffQuoteId ?? '',
          payoffAmount: payoffData?.payoffAmount ?? null,
          payoffPerDiem: perDiemValue ?? null,
          payoffGoodDate: payoffGoodDate,
          totalPayoffAmount: (totalPayoffValue ?? payoffData?.totalPayoffAmount) as number ?? null,
          dtTransactionFee: payoffData?.dtTransactionFee ?? null,
          dtTaxes: payoffData?.dtTaxes ?? null,
          dtTotalDue: dtTotalDueValue ?? null,
          customerRefundAmount: dtRefundDueValue ?? null,
          totalAdjustment: totalAdjustmentValue ?? null,
          atTitleIsReleasable: payoffData?.atTitleIsReleasable
        }
      });
    },
    [payoffData, salesPrice],
  );

  const isDisable = useMemo(() => (disableFields || !hasAdminAccountingCSRole), [disableFields, hasAdminAccountingCSRole]);

  const payoffQuoteFormConfig = useMemo(
    () => [
      {
        name: 'payOffData.payoffAmount',
        label: 'Payoff Amount',
        decimalScale: 2,
        prefix: '$',
        disabled: isDisable,
        component: NumberInputField,
      },
      {
        name: 'payOffData.payoffPerDiem',
        label: 'Per Diem',
        decimalScale: 2,
        prefix: '$',
        disabled: isDisable,
        component: NumberInputField,
      },
      {
        name: 'payOffData.payoffGoodDate',
        label: 'Good Thru',
        component: SelectDateField,
        disabled: isDisable,
      },
      {
        name: 'payOffData.totalPayoffAmount',
        label: 'Total Payoff',
        decimalScale: 2,
        prefix: '$',
        disabled: isDisable,
        component: NumberInputField
      },
      {
        name: 'payOffData.dtTransactionFee',
        label: 'DT Transaction Fee',
        decimalScale: 2,
        prefix: '$',
        disabled: true,
        component: NumberInputField
      },
      {
        name: 'payOffData.dtTaxes',
        label: 'DT Taxes',
        decimalScale: 2,
        prefix: '$',
        disabled: true,
        component: NumberInputField
      },
      {
        name: 'payOffData.dtTotalDue',
        label: 'DT Total Due',
        decimalScale: 2,
        prefix: '$',
        disabled: true,
        component: NumberInputField
      },
      {
        name: 'payOffData.customerRefundAmount',
        label: 'DT Refund Due',
        decimalScale: 2,
        prefix: '$',
        disabled: true,
        component: NumberInputField
      },
      {
        name: 'payOffData.totalAdjustment',
        label: 'Total Adjustment',
        decimalScale: 2,
        prefix: '$',
        disabled: true,
        component: NumberInputField,
      },
    ],
    [isDisable]
  );

  const addAdjustmentFormConfig = useMemo(() => [
    {
      name: 'adjustments.amountToFinanceFlat',
      label: 'Amount to Finance/Flat',
      disabled: !hasAdminAccountingRole,
      component: NumberInputField,
      allowNegative: true,
      prefix: '$',
      decimalScale: 2,
    },
    {
      name: 'adjustments.adjustedPayoff',
      label: 'Adjusted Payoff',
      disabled: !hasAdminAccountingRole,
      component: NumberInputField,
      allowNegative: true,
      prefix: '$',
      decimalScale: 2,
    },
    {
      name: 'adjustments.totalAdjustment',
      label: 'Total Adjustment',
      disabled: true,
      allowNegative: true,
      prefix: '$',
      decimalScale: 2,
      component: NumberInputField,
    },
  ], [hasAdminAccountingRole]);

  useEffect(() => {
    if (asset?.assetLienHolder) {
      const isATSupportedAndReleasable = asset?.assetLienHolder.isAcceleratedTitleSupported && asset?.assetLienHolder.acceleratedTitleTitleIsReleasable;
      const enableFinalPayoffButton = isATSupportedAndReleasable && !(asset?.assetLienHolder?.isPayoffSubmitted ?? false);
      const enableSubmitButton = enableFinalPayoffButton && (asset?.assetLienHolder?.isFinalPayoffQuote ?? false);
      const enableCancel = (asset?.assetLienHolder?.isPayoffSubmitted ?? false) && !enableSubmitButton;

      setShowFirstCustomButton(enableFinalPayoffButton || false);
      setShowSecondCustomButton(enableSubmitButton || false);
      setShowCancelButton(enableCancel);
    }
    else {
      setShowFirstCustomButton(false);
      setShowSecondCustomButton(false);
      setShowCancelButton(false);
    }
  }, [asset?.assetLienHolder]);

  const firstButtonClick = useCallback(async () => {
    setIsLoading(true);
    let infoMessage = 'Payoff Quote pulled.';
    let alertMessage = '';
    const response = await requestPullFinalPayoffQuote(queryParams()?.dealId);
    if (response && response.payoffMsg) {
      setAlertMessagee(response.payoffMsg);
      setShowAlert(response.payoffMsg !== '');
      dispatch(setPayoffQuoteResponse(response));
    } else if (response) {

      setPayoffData((prev) => ({
        ...prev,
        payoffAmount: response.payoffAmount,
        payoffPerDiem: response.dollarDaysRatio,
        payoffGoodDate: new Date(response.goodUntilDate),
        dtTransactionFee: response.atTransactionCharges,
        dtTaxes: response.atTaxFee,
        payoffQuoteId: response.payoffQuoteId,
        atTitleIsReleasable: response.atTitleIsReleasable,
        dealId: params?.dealId
      }));

      setDisableFields((response?.isATSupported ?? false) && (response?.atTitleIsReleasable ?? false));

      if (response.titleContent) {
        openPdf(response?.titleContent);
      }
      else {
        infoMessage = infoMessage + '\n Title Document unavailable.';
      }
      if (response?.isInstructionsFileExist)
        infoMessage = infoMessage + '\n Payoff Instructions unavailable.';

      if (response?.payoffAmount <= 0 || response?.dollarDaysRatio <= 0 || response?.goodUntilDate === '')
        alertMessage = alertMessage + '\n Amount, Per Diem, Good Date are required fields for payoff quote.';

      if (response?.atTitleIsReleasable === false) {
        alertMessage = alertMessage + '\n AT Title Not Releasable';
      }

      if (alertMessage !== '') {
        const additonalMessage = '';
        setAlertMessagee(`${alertMessage} \n ${additonalMessage}`);
        setShowAlert(alertMessage !== '');
      }
      else if (infoMessage)
        Notistack.infoMessage(infoMessage);
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      setShowSecondCustomButton(true);
    }
    setIsLoading(false);
  }, [queryParams()?.dealId]);

  const secondButtonClick = useCallback(() => {
    setShowConfirmationModal(true);
  }, []);

  const handleDealSheetAdjustment = useCallback(
    async (data: IDealSheetAdjustment, isOverride: boolean) => {
      const adjustmentThreshold = (payoffData?.adjustmentThreshold ?? 0) as number;
      if (((data?.adjPayOff ?? 0) < adjustmentThreshold) || isOverride) {
        await createDealSheetAdjustment(data);
        mutateDealsheetAdjustment();
        return 1;
      }
      setAlertMessagee(`Adjustment is above threshold of $${adjustmentThreshold}, please send to Adjusted Payoff SM Approval.`);
      setShowAlert(true);
      return 0;
    },
    [payoffData?.adjustmentThreshold]
  );
  const handleAddAdjustment = useCallback(
    async (data: IAddAdjustment) => {
      setIsLoading(true);
      const createData: IDealSheetAdjustment = {
        dealId: params?.dealId ?? '',
        contactId: params?.id ?? '',
        amountFinanced: data.amountToFinanceFlat ?? 0,
        adjPayOff: data.adjustedPayoff ?? 0,
        totalAdjustment: data.totalAdjustment ?? 0,
      };
      const resp = await createDealSheetAdjustment(createData);
      mutateDealsheetAdjustment();
      resp && formikRef?.current?.resetForm({ values: payoffQuoteData });
      setIsLoading(false);
    },
    [params?.dealId, params?.id, payoffQuoteData]
  );

  const handleModelConfirm = useCallback(async (isOverride = false) => {
    setIsLoading(true);
    setShowConfirmationModal(false);
    if (confirmationModalText === EConfirmationModalText.SubmitPayoffConfirmation) {
      if ((payoffQuoteData.payOffData.totalAdjustment ?? 0) > 0) {
        const createData = {
          dealId: params?.dealId ?? '',
          contactId: params?.id ?? '',
          amountFinanced: 0,
          adjPayOff: payoffQuoteData.payOffData.totalAdjustment ?? 0,
          totalAdjustment: 0 - (payoffQuoteData.payOffData.totalAdjustment ?? 0),
        };
        const resp = await handleDealSheetAdjustment(createData, isOverride);
        if (resp) {
          const submitPayoffResponse = await requestSubmitPayoffQuote(
            queryParams()?.dealId,
            payoffQuoteData.payOffData.customerRefundAmount
          );
          if (submitPayoffResponse) {
            mutate();
            setShowConfirmationModal(false);
            dispatch(setLastCommentsRefresh(new Date().toISOString()));
          } else {
            setConfirmationModalText(EConfirmationModalText.RemoveDealFromAT);
          }
          setShowAlert(false);
        }
      } else {
        const submitPayoffResponse = await requestSubmitPayoffQuote(params?.dealId, payoffQuoteData.payOffData.customerRefundAmount);
        if (submitPayoffResponse) {
          mutate();
          setShowConfirmationModal(false);
          dispatch(setLastCommentsRefresh(new Date().toISOString()));
        } else {
          setConfirmationModalText(EConfirmationModalText.RemoveDealFromAT);
        }
      }
    } else {
      await requestUpdateAssetLienholder(params?.id, assetId);
      setShowConfirmationModal(false);
    }
    setIsLoading(false);
  }, [params?.dealId, params?.id, payoffQuoteData.payOffData.customerRefundAmount, assetId, confirmationModalText, payoffQuoteData.payOffData.totalAdjustment, handleDealSheetAdjustment]);

  const handleCancelClick = useCallback(() => {
    setshowCancelModal(true);
  }, []);

  const handleCancelModelConfirm = useCallback(async () => {
    setIsLoading(true);
    setshowCancelModal(false);
    const response = await requestCancelPayoff(queryParams()?.dealId ?? '');
    if (response?.succeed) {
      mutate();
    }
    dispatch(setLastCommentsRefresh(new Date().toISOString()));
    setIsLoading(false);
  }, [queryParams()?.dealId]);

  const handleSave = useCallback(
    async (data: IFinanceData) => {
      setIsLoading(true);
      const requestedPayload = {
        ...data.payOffData,
        dealId: params?.dealId ?? '',
      };
      await savePayoffQuote(requestedPayload, params?.id, asset?.id);
      setIsLoading(false);
    },
    [params?.dealId, params?.id, asset?.id]
  );

  const loading = useMemo(() => ((!error && isValidating) || payoffLoading) || isLoading, [isLoading, payoffLoading, error, isValidating]);

  return (
    <>
      <Modal
        hideBackdrop
        title='Finance'
        open={!!financeModalPostition}
        resetPosition={financeModalPostition}
        dialogWidth={'1200px'}
        onClose={handleClose}
      >
        <LoaderBackdrop open={loading} handleClose={() => setIsLoading(false)} />
        <ModalWrapper>
          <Formik enableReinitialize initialValues={payoffQuoteData} innerRef={formikRef} onSubmit={handleSave}>
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <FinanceForm
                      formTitle={'Payoff Quote'}
                      formConfig={payoffQuoteFormConfig}
                      holidays={holidays}
                      salesPrice={salesPrice}
                      buttonText='Save'
                      disableSubmitButton={!hasAdminAccountingCSRole}
                      buttonWidth={'60px'}
                      firstCustomButtonText={(showFirstCustomButton && hasPullFinalPayoffRole && workflowCurrentStep?.workflowName === 'RTF' && (allowedSteps.includes(workflowCurrentStep?.workflowStepName || '')) ? 'Pull Final Payoff Quote' : '')}
                      secondCustomButtonText={(showSecondCustomButton && hasAcumaticaATSubmitRole) ? 'Submit Payoff Quote' : ''}
                      cancelButtonText={showCancelButton ? 'Cancel' : ''}
                      formProps={props}
                      handleFirstButtonClick={firstButtonClick}
                      handleSecondButtonClick={secondButtonClick}
                      handleCancelClick={handleCancelClick}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FinanceForm
                      formTitle={'Add Adjustment'}
                      formConfig={addAdjustmentFormConfig}
                      salesPrice={salesPrice}
                      buttonText={'Add Adjustment'}
                      disableSubmitButton={!hasAdminAccountingRole || (!props.values?.adjustments?.adjustedPayoff && !props.values?.adjustments?.amountToFinanceFlat)}
                      buttonWidth={'148px'}
                      formProps={props}
                      handleAddAdjustment={handleAddAdjustment}
                    />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>

          <Typography variant="h2" mt="22px" mb="10px" color={colorTheme.palette.common.black} >
            Adjustments
          </Typography>
          <PhoenixGrid
            withHeader
            canNotCopy
            canNotEdit
            noButton
            autoHeight
            isRowSelectable={() => false}
            columns={adjustmentsGridColumns}
            pageSize={4}
            rows={dealsheetAdjustment || []}
            getRowId={(row) => row.dealSheetAdjustmentId}
          />
        </ModalWrapper>
      </Modal>
      <AlertModal
        title="Alert"
        hideCancel={!hasAdminAccountingRole}
        subtitle={alertMessage}
        buttonText={hasAdminAccountingRole ? 'Override' : 'OK'}
        cancelButtonText='OK'
        isLoading={isLoading}
        open={showAlert}
        onClose={() => setShowAlert(false)}
        onConfirm={() => hasAdminAccountingRole ? handleModelConfirm(true) : setShowAlert(false)}
      />
      <AlertModal
        open={showConfirmationModal}
        title="Alert"
        subtitle={confirmationModalText}
        buttonText="Yes"
        cancelButtonText="No"
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleModelConfirm}
      />
      <AlertModal
        open={showCancelModal}
        title="Alert"
        buttonText="Yes"
        cancelButtonText="No"
        subtitle={EConfirmationModalText.CancelPayOffConfirmation}
        onClose={() => setshowCancelModal(false)}
        onConfirm={handleCancelModelConfirm}
      />
    </>
  );
};
